import React, { useState, useEffect } from 'react';
import { TrendingUp, CheckCircle, Info, Send } from 'lucide-react';

const FloatingDemoWindow = () => {
  const [messages, setMessages] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentScenario, setCurrentScenario] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [buttonPressed, setButtonPressed] = useState(false);

  const demoScenarios = [
    // Scenario 1: Simple stock purchase
    [
      { type: 'typing', delay: 1500 },
      { type: 'user', text: 'Buy 10 shares of Reliance at market', delay: 2000 },
      { 
        type: 'draft', 
        text: 'Reliance Industries Ltd',
        price: '₹2,847.50',
        quantity: '10 shares',
        total: '₹28,475.00',
        tradeType: 'Buy',
        delay: 2500 
      },
      { type: 'confirm-button', delay: 2000 },
      { type: 'button-press', delay: 1500 },
      { type: 'confirmation', text: '✓ Trade executed successfully', delay: 2000 }
    ],
    // Scenario 2: Sell 50% of IT stocks
    [
      { type: 'typing', delay: 1500 },
      { type: 'user', text: 'Sell 50% of my IT stocks', delay: 2000 },
      { 
        type: 'multi-draft', 
        orders: [
          {
            stock: 'TCS',
            quantity: '2 shares',
            price: '₹4,125.00',
            total: '₹8,250.00'
          },
          {
            stock: 'Infosys',
            quantity: '3 shares',
            price: '₹1,893.45',
            total: '₹5,680.35'
          }
        ],
        totalValue: '₹13,930.35',
        delay: 3000 
      },
      { type: 'confirm-button', delay: 2000 },
      { type: 'button-press', delay: 1500 },
      { type: 'confirmation', text: '✓ Orders executed successfully', delay: 2000 }
    ],
    // Scenario 3: Options straddle
    [
      { type: 'typing', delay: 1500 },
      { type: 'user', text: 'Create a simple NIFTY straddle for next week', delay: 2000 },
      { 
        type: 'options-draft', 
        strategy: 'Long Straddle',
        underlying: 'NIFTY 50',
        expiry: '30 Jan 2025',
        strike: '24,850',
        callLeg: {
          type: 'Call',
          strike: '24,850',
          premium: '₹142',
          quantity: '1 Lot (50 qty)'
        },
        putLeg: {
          type: 'Put',
          strike: '24,850',
          premium: '₹138',
          quantity: '1 Lot (50 qty)'
        },
        totalCost: '₹14,000',
        delay: 3000 
      },
      { type: 'confirm-button', delay: 2000 },
      { type: 'button-press', delay: 1500 },
      { type: 'confirmation', text: '✓ Straddle strategy executed', delay: 2000 }
    ]
  ];

  useEffect(() => {
    const currentScenarioSteps = demoScenarios[currentScenario];
    
    if (currentStep >= currentScenarioSteps.length) {
      // Move to next scenario after completion
      const resetTimer = setTimeout(() => {
        setMessages([]);
        setCurrentStep(0);
        setIsTyping(false);
        setButtonPressed(false);
        setCurrentScenario((prev) => (prev + 1) % demoScenarios.length);
      }, 3000);
      return () => clearTimeout(resetTimer);
    }

    const message = currentScenarioSteps[currentStep];
    
    const timer = setTimeout(() => {
      if (message.type === 'typing') {
        setIsTyping(true);
      } else if (message.type === 'button-press') {
        setButtonPressed(true);
        setTimeout(() => setButtonPressed(false), 300);
      } else {
        setIsTyping(false);
        setMessages(prev => [...prev, message]);
      }
      setCurrentStep(prev => prev + 1);
    }, message?.delay || 2000);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, currentScenario]);

  return (
    <div className="mobile-demo-window">
      {/* Mobile phone frame */}
      <div className="mobile-frame">
        <div className="mobile-notch"></div>
        
        <div className="demo-window-header">
          <div className="demo-time">9:41</div>
          <div className="demo-window-title">
            <span className="demo-status-indicator"></span>
            AiStonks
          </div>
          <div className="demo-signal-icons">
            <span>📶</span>
            <span>🔋</span>
          </div>
        </div>
        
        <div className="demo-window-content">
          {messages.map((msg, idx) => (
            <div key={idx} className={`demo-message ${msg.type}`}>
              {msg.type === 'user' && (
                <div className="demo-message-bubble user-message">
                  <p>{msg.text}</p>
                </div>
              )}
              
              {msg.type === 'draft' && (
                <div className="demo-trade-draft">
                  <div className="draft-header">
                    <TrendingUp className="w-4 h-4" />
                    <span className="draft-title">Draft Order</span>
                  </div>
                  <div className="draft-body">
                    <div className="draft-row">
                      <span className="draft-label">Stock</span>
                      <span className="draft-value">{msg.text}</span>
                    </div>
                    <div className="draft-row">
                      <span className="draft-label">Type</span>
                      <span className="draft-value trade-type-buy">{msg.tradeType}</span>
                    </div>
                    <div className="draft-row">
                      <span className="draft-label">Price</span>
                      <span className="draft-value">{msg.price}</span>
                    </div>
                    <div className="draft-row">
                      <span className="draft-label">Quantity</span>
                      <span className="draft-value">{msg.quantity}</span>
                    </div>
                    <div className="draft-row total-row">
                      <span className="draft-label">Total</span>
                      <span className="draft-value total-value">{msg.total}</span>
                    </div>
                  </div>
                </div>
              )}

              {msg.type === 'multi-draft' && (
                <div className="demo-trade-draft multi-order">
                  <div className="draft-header">
                    <TrendingUp className="w-4 h-4" />
                    <span className="draft-title">Draft Orders (Sell)</span>
                  </div>
                  <div className="draft-body">
                    {msg.orders.map((order, i) => (
                      <div key={i} className="multi-order-item">
                        <div className="order-stock-header">{order.stock}</div>
                        <div className="order-details-row">
                          <span className="order-detail">{order.quantity}</span>
                          <span className="order-detail">@ {order.price}</span>
                          <span className="order-total">{order.total}</span>
                        </div>
                      </div>
                    ))}
                    <div className="draft-row total-row">
                      <span className="draft-label">Total Value</span>
                      <span className="draft-value total-value">{msg.totalValue}</span>
                    </div>
                  </div>
                </div>
              )}

              {msg.type === 'options-draft' && (
                <div className="demo-trade-draft options-draft">
                  <div className="draft-header">
                    <Info className="w-4 h-4" />
                    <span className="draft-title">{msg.strategy}</span>
                  </div>
                  <div className="draft-body">
                    <div className="draft-row-compact">
                      <span className="draft-label">Underlying</span>
                      <span className="draft-value">{msg.underlying}</span>
                    </div>
                    <div className="draft-row-compact">
                      <span className="draft-label">Strike</span>
                      <span className="draft-value">{msg.strike}</span>
                    </div>
                    <div className="draft-row-compact">
                      <span className="draft-label">Expiry</span>
                      <span className="draft-value">{msg.expiry}</span>
                    </div>
                    
                    <div className="options-legs">
                      <div className="option-leg">
                        <div className="leg-header">Call</div>
                        <div className="leg-details">
                          <span>{msg.callLeg.strike}</span>
                          <span>{msg.callLeg.premium}</span>
                        </div>
                      </div>
                      <div className="option-leg">
                        <div className="leg-header">Put</div>
                        <div className="leg-details">
                          <span>{msg.putLeg.strike}</span>
                          <span>{msg.putLeg.premium}</span>
                        </div>
                      </div>
                    </div>

                    <div className="draft-row total-row">
                      <span className="draft-label">Total Cost</span>
                      <span className="draft-value total-value">{msg.totalCost}</span>
                    </div>
                  </div>
                </div>
              )}

              {msg.type === 'confirm-button' && (
                <div className="confirm-button-container">
                  <button className="demo-cancel-button">
                    Cancel
                  </button>
                  <button className={`demo-confirm-button ${buttonPressed ? 'button-pressed' : ''}`}>
                    Confirm Trade
                  </button>
                </div>
              )}
              
              {msg.type === 'confirmation' && (
                <div className="demo-message-bubble confirmation-message">
                  <CheckCircle className="w-4 h-4" />
                  <span>{msg.text}</span>
                </div>
              )}
            </div>
          ))}
          
          {/* Typing Indicator */}
          {isTyping && (
            <div className="typing-indicator">
              <div className="typing-dot"></div>
              <div className="typing-dot"></div>
              <div className="typing-dot"></div>
            </div>
          )}
          
          {messages.length === 0 && !isTyping && (
            <div className="demo-placeholder">
              <p>Start typing to trade...</p>
            </div>
          )}
        </div>
        
        {/* Mobile Input Area */}
        <div className="mobile-input-area">
          <input 
            type="text" 
            className="mobile-input" 
            placeholder="Type a command..."
            disabled
          />
          <button className="mobile-send-button">
            <Send />
          </button>
        </div>
      </div>
    </div>
  );
};

export default FloatingDemoWindow;
